export type PortalMapPreview = { embedUrl: string; label: string | null };

const googleMapHosts = new Set([
  "google.com", "www.google.com", "maps.google.com",
  "google.cz", "www.google.cz", "maps.google.cz",
  "google.sk", "www.google.sk", "maps.google.sk",
]);

function parseMapUrl(value: string | null | undefined): URL | null {
  if (!value || value.length > 4096) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== "https:" || url.username || url.password || url.port) return null;
    return url;
  } catch { return null; }
}

export function mapPreviewForAddress(address: string | null | undefined): PortalMapPreview | null {
  const query = address?.trim();
  if (!query || query.length > 1000) return null;
  const embed = new URL("https://www.google.com/maps");
  embed.searchParams.set("q", query);
  embed.searchParams.set("output", "embed");
  return { embedUrl: embed.href, label: query };
}

/** Only these short-link endpoints may be requested by the server resolver. */
export function isGoogleMapShortLink(value: string | null | undefined): boolean {
  const url = parseMapUrl(value);
  if (!url || url.hash) return false;
  if (["share.google", "maps.app.goo.gl"].includes(url.hostname)) {
    return /^\/[a-zA-Z0-9_-]{5,120}\/?$/.test(url.pathname) && !url.search;
  }
  if (url.hostname === "goo.gl") return /^\/maps\/[a-zA-Z0-9_-]{5,120}\/?$/.test(url.pathname) && !url.search;
  return ["google.com", "www.google.com"].includes(url.hostname)
    && url.pathname === "/share.google"
    && [...url.searchParams.keys()].every((key) => key === "q")
    && /^[a-zA-Z0-9_-]{5,120}$/.test(url.searchParams.get("q") ?? "");
}

/** Convert known map URLs to Google embeds; never embed an arbitrary website. */
export function mapPreviewFromUrl(value: string | null | undefined): PortalMapPreview | null {
  const url = parseMapUrl(value);
  if (!url || !googleMapHosts.has(url.hostname)) return null;
  if (url.pathname === "/maps/embed") {
    const configuration = url.searchParams.get("pb");
    if (configuration) {
      const embed = new URL("https://www.google.com/maps/embed");
      embed.searchParams.set("pb", configuration);
      return { embedUrl: embed.href, label: null };
    }
  }
  if (url.pathname !== "/search" && url.pathname !== "/maps" && !url.pathname.startsWith("/maps/")) return null;

  const query = url.searchParams.get("q") ?? url.searchParams.get("query") ?? url.searchParams.get("destination");
  if (query) return mapPreviewForAddress(query);

  // Marker coordinates identify a place; /@ coordinates can merely be the map viewport.
  const coordinates = url.pathname.match(/!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/);
  if (coordinates && Math.abs(Number(coordinates[1])) <= 90 && Math.abs(Number(coordinates[2])) <= 180) {
    return mapPreviewForAddress(`${coordinates[1]},${coordinates[2]}`);
  }
  const place = url.pathname.match(/^\/maps\/place\/([^/]+)/)?.[1];
  if (place) {
    try { return mapPreviewForAddress(decodeURIComponent(place.replace(/\+/g, " "))); } catch { return null; }
  }
  return null;
}
